import { styled } from "../../../styles";

/**
 * Styles for the /admin Translations group — Survey Translations (EN-5163).
 * Generic admin chrome lives in `shared/AdminPage.styles.ts`.
 */

export const ModalBodyText = styled("p", {
  margin: 0,
  fontSize: "14px",
  lineHeight: 1.5,
  color: "$charcoal",
  fontFamily: "$openSans",
  // Translation keys are single unbreakable tokens (underscores, no spaces)
  // long enough to force the modal into horizontal scroll — let them wrap.
  overflowWrap: "anywhere",
});

/** Reports what a maintenance action actually did (EN-5152 convention). */
export const ResultNotice = styled("p", {
  margin: 0,
  padding: "10px 12px",
  borderRadius: "6px",
  backgroundColor: "$success-lightest",
  color: "$success-dark",
  fontFamily: "$openSans",
  fontSize: "13px",
});

/**
 * The key a translation is stored under — a name plus a GUID. Capped and
 * ellipsised (the full key is on the cell's `title`) so it can't take the
 * width the Value editor needs; table layout is auto, so the longest key in
 * the survey otherwise sets the column (EN-5146 UI feedback).
 */
export const TranslationKey = styled("span", {
  display: "block",
  maxWidth: "360px",
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
  fontFamily: "$openSans",
  fontSize: "13px",
  color: "$gray-100",
});

/**
 * The editable cell in the translation grid. Auto-height would reflow the
 * table on every keystroke, so it is a fixed box that scrolls — three lines
 * rather than two, and at body size: this is the field the page exists for
 * and it was the smallest thing on the screen (EN-5146 UI feedback).
 */
export const TranslationInput = styled("textarea", {
  width: "100%",
  minWidth: "320px",
  height: "64px",
  resize: "vertical",
  padding: "8px 10px",
  borderRadius: "4px",
  border: "1px solid $gray-40",
  backgroundColor: "$white",
  color: "$charcoal",
  fontFamily: "$openSans",
  fontSize: "14px",
  lineHeight: 1.45,

  "&:focus": {
    outline: "none",
    borderColor: "$primary",
  },

  "&:disabled": {
    backgroundColor: "$gray-20",
    cursor: "not-allowed",
  },

  variants: {
    /** An edited row that hasn't been saved yet. */
    dirty: {
      true: {
        borderColor: "$warning",
        backgroundColor: "$warning-lightest",
      },
    },
  },
});

/** Sticky action bar that appears once there is something to save. */
export const UnsavedBar = styled("div", {
  position: "sticky",
  top: 0,
  zIndex: 2,
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: "16px",
  padding: "10px 14px",
  borderRadius: "8px",
  border: "1px solid $warning",
  backgroundColor: "$warning-lightest",
});

export const UnsavedCount = styled("span", {
  fontFamily: "$openSans",
  fontSize: "13px",
  color: "$charcoal",
});

export const UnsavedActions = styled("div", {
  display: "flex",
  gap: "8px",
});

/** Empty state before a survey is picked. */
export const SelectionPrompt = styled("p", {
  margin: 0,
  padding: "48px 16px",
  textAlign: "center",
  fontFamily: "$openSans",
  fontSize: "14px",
  color: "$gray-80",
  borderRadius: "8px",
  border: "1px dashed $gray-40",
  backgroundColor: "$white",
});

/**
 * A count in the sync summary table (EN-5330). Clicking it filters the key
 * list to the keys behind the count; the selected one stays highlighted.
 */
export const SummaryCountButton = styled("button", {
  minWidth: "40px",
  padding: "2px 8px",
  borderRadius: "4px",
  border: "1px solid transparent",
  backgroundColor: "transparent",
  color: "$primary",
  fontFamily: "$openSans",
  fontSize: "13px",
  textAlign: "right",
  textDecoration: "underline",
  cursor: "pointer",

  "&:hover": {
    backgroundColor: "$gray-20",
  },

  "&:focus-visible": {
    outline: "none",
    borderColor: "$primary",
  },

  variants: {
    selected: {
      true: {
        borderColor: "$primary",
        backgroundColor: "$primary-20",
        textDecoration: "none",
      },
    },
  },
});

/** A zero count — nothing behind it to filter to. */
export const SummaryCountZero = styled("span", {
  display: "inline-block",
  minWidth: "40px",
  padding: "2px 8px",
  textAlign: "right",
  fontFamily: "$openSans",
  fontSize: "13px",
  color: "$gray-80",
});

/** "Showing: de-DE · Missing vs English" with a clear action. */
export const ActiveCellFilter = styled("div", {
  display: "flex",
  alignItems: "center",
  gap: "12px",
  padding: "8px 12px",
  borderRadius: "6px",
  backgroundColor: "$primary-20",
  fontFamily: "$openSans",
  fontSize: "13px",
  color: "$charcoal",
});

/** "Differs only in whitespace" under a sync row's status badge (EN-5330). */
export const DifferenceHint = styled("span", {
  display: "block",
  marginTop: "4px",
  fontFamily: "$openSans",
  fontSize: "12px",
  color: "$gray-80",
});

/**
 * Page-scope controls of the sync page (used location + "Sync all pending"),
 * side by side on the page background rather than in a card (EN-5330).
 */
export const PageScopeBar = styled("div", {
  display: "flex",
  flexWrap: "wrap",
  alignItems: "flex-end",
  gap: "16px",

  // Line the button up with the select beside it, as FiltersActions does.
  "& > button": {
    height: "40px",
  },
});
